import React from "react";
import PropTypes from "prop-types";

/**
 * PersonHeader — the presentational header shell for person-shaped profiles
 * (PR Profile, Expert, Media Profile, PR/Expert hybrid). A single bordered identity card
 * (Alyssa's `EL-406ae701` "Page header"): an optional cover/banner band, an avatar column that
 * overlaps it, a main identity column (name, headline, inline edit pens), and an optional
 * top-right actions row.
 *
 * The person-shaped sibling of `<EntityHeader>` — same `banner` slot contract, mirroring
 * `<EntityHeader>`'s already-proven cover/banner band: each person engine now carries its own
 * per-type banner through this slot. `aside` has been replaced by `actions`, rendered top-right
 * below the band rather than beside `identity`: viewers get [Follow] [Share Profile], owners/
 * admins get [Share Profile] [kebab]. Contact details and the message action no longer live in
 * the header at all — they render in the rail's Connect card. Like `<EntityHeader>` this is a
 * layout shell only: it holds NO engine logic; the engine passes already-built slots. The header
 * an engine uses is chosen by a person-shaped trait, not by model class.
 *
 * Introduced by the PR Profile page; each later consumer is expected to refine this
 * contract — normal evolution of the shared abstraction.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.avatar - Avatar slot (e.g. AvatarWithLabel)
 * @param {React.ReactNode} props.identity - Main identity column (name, headline, links, inline pens)
 * @param {React.ReactNode} [props.actions] - Optional top-right actions row (Follow/Share for viewers,
 *   Share/kebab for owners)
 * @param {React.ReactNode} [props.banner] - Cover/banner band slot (rendered only when provided)
 * @param {string} [props.avatarWrapperClassName] - Extra classes for the avatar column (engine CSS hook)
 */
function PersonHeader({ avatar, identity, actions = null, banner = null, avatarWrapperClassName = "" }) {
  const hasBanner = banner != null;

  return (
    <div
      className={`card person-header-card${hasBanner ? " person-header-card--with-banner" : ""} animated faster fadeIn`}
      style={{ position: "relative" }}
    >
      {banner}

      <div className="person-header-content p-4">
        <div className="row">
          <div
            className={`col-12 col-sm-auto d-flex justify-content-center mb-3 mb-sm-0 ${avatarWrapperClassName}`.trim()}
          >
            {avatar}
          </div>

          <div className="col">
            {/* The actions sit right of the identity while it keeps ~24rem, then wrap under it. */}
            <div className="person-header-main d-flex flex-wrap align-items-start">
              <div className="person-header-identity">{identity}</div>
              {actions != null && (
                <div className="person-header-actions d-flex flex-wrap align-items-start">{actions}</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

PersonHeader.propTypes = {
  avatar: PropTypes.node.isRequired,
  identity: PropTypes.node.isRequired,
  actions: PropTypes.node,
  banner: PropTypes.node,
  avatarWrapperClassName: PropTypes.string,
};

export default PersonHeader;
